import { processImageServiceResponse, getImageType, setDefaultBackgroundColor} from "src/utils/common";
import HttpClient from "../../services/HttpClient";
import Notification from "../../utils/Notification";
import configs from "src/configs";
import { barcodeFormatConstants } from "src/utils/constants";

class PreviewManager {

  constructor(httpClient = null, defaultImageConfig = {}, v1ImageConfig = {}) {
    this.httpClient = httpClient || new HttpClient()
    console.log(this.httpClient)
    this.url = `/qr/image/configs/preview`
    this.defaultImageConfig = defaultImageConfig
    this.v1ImageConfig = v1ImageConfig
  }

  async hexToCMYK(hex) {
    // Ensure the hex string is in the correct format
    if (hex.charAt(0) === '#') {
      hex = hex.substring(1);
    }

    // Parse the hex color to get RGB values
    let r = parseInt(hex.substring(0, 2), 16) / 255;
    let g = parseInt(hex.substring(2, 4), 16) / 255;
    let b = parseInt(hex.substring(4, 6), 16) / 255;

    // Convert RGB to CMY
    let c = 1 - r;
    let m = 1 - g;
    let y = 1 - b;

    // Find the minimum value of C, M, and Y
    let k = Math.min(c, m, y);

    // Convert CMY to CMYK
    if (k === 1) {
      c = 0;
      m = 0;
      y = 0;
    } else {
      c = (c - k) / (1 - k);
      m = (m - k) / (1 - k);
      y = (y - k) / (1 - k);
    }

    // Convert to percentages and format as CMYK string
    c = Math.round(c * 100);
    m = Math.round(m * 100);
    y = Math.round(y * 100);
    k = Math.round(k * 100);
    return `cmyk(${c}%, ${m}%, ${y}%, ${k}%)`;
  }
  async convertCMYKStringsToArray(obj) {
    for (const key in obj) {

      if (typeof obj[key] === 'string' && obj[key].startsWith('cmyk(')) {
        // Extract the CMYK values from the string
        const cmykString = obj[key].slice(5, -1);
        const cmykArray = cmykString.split(',').map(value => parseInt(value));

        // Update the object with the array of integers
        obj[key] = cmykArray;
      }
    }
    return obj;
  }
  async convertHexInPayloadToCMYK(obj) {
    // Regular expression to check if a string is a valid hex color
    const hexColorRegex = /^#([0-9A-F]{3}){1,2}$/i;

    for (let key in obj) {
      if (typeof obj[key] === 'string' && hexColorRegex.test(obj[key])) {
        obj[key] = await this.hexToCMYK(obj[key]);
      }
    }

    return obj;
  }

  processImageFormat(updatedConfigWithBase, QRImageFormData, isDownload, isSavingConfig) {
    // Convert postscript to eps
    if(QRImageFormData.imageFormat === "postscript"){
      updatedConfigWithBase.imageFormat = "eps"
    }

    // Force JPG for CMYK previews/saves — browsers cannot display EPS/postscript.
    // This must run after the postscript→eps conversion to override it for non-downloads.
    if(QRImageFormData.colorMode === "cmyk" && (QRImageFormData.imageFormat == null || !isDownload)){
      updatedConfigWithBase.imageFormat = "jpg"
    }

    // If backgroundImage ends with jpeg or jpg, change imageFormat to jpg
    if(QRImageFormData.backgroundImage && typeof QRImageFormData.backgroundImage === 'string') {
      const lowerBackgroundImage = QRImageFormData.backgroundImage.toLowerCase()
      if(lowerBackgroundImage.endsWith('.jpeg') || lowerBackgroundImage.endsWith('.jpg')) {
        updatedConfigWithBase.imageFormat = "jpg"
      }
    }

    // Convert incompatible RGB formats to PNG for preview/save
    if(updatedConfigWithBase.colorMode === "rgb" && ['eps', 'pdf', 'tiff', 'gif', 'svg', 'ai'].includes(updatedConfigWithBase.imageFormat) && !isDownload && !isSavingConfig) {
      updatedConfigWithBase.imageFormat = "png"
    }

    return updatedConfigWithBase
  }

  /**
   * For vector formats (eps/pdf/ai) the API returns image-info based on the
   * vector canvas size, but the preview request is always rasterised (png/jpg).
   * Recalculate all size fields so the UI shows dimensions matching the
   * user-selected format, not the raster surrogate.
   */
  correctPreviewImageInfo(previewImage, originalImageFormat, payload) {
    const VECTOR_FORMATS = ['eps', 'pdf', 'ai', 'postscript'];
    if (!VECTOR_FORMATS.includes((originalImageFormat || '').toLowerCase())) {
      return previewImage;
    }
    const { qrWidthPixel, modulePerSide, quietZone } = previewImage;
    if (qrWidthPixel == null || modulePerSide == null || quietZone == null) {
      return previewImage;
    }
    const resolution = payload.resolution ? Number(payload.resolution) : 72;
    const bwr = payload.bwr ? Number(payload.bwr) : 0;
    // Convert bwr to pixels based on unit (mm already converted to cm upstream)
    let bwrInPx = 0;
    if (bwr) {
      const unit = payload.unitsOfMeasure || 'cm';
      if (unit === 'inch') {
        bwrInPx = bwr * resolution;
      } else {
        // cm
        bwrInPx = (bwr / 2.54) * resolution;
      }
    }

    const correctedModuleWidthPx = qrWidthPixel / (modulePerSide + 2 * quietZone);
    const moduleWidthAfterBwr = correctedModuleWidthPx - bwrInPx;

    return {
      ...previewImage,
      moduleWidthInPixel: Number(correctedModuleWidthPx.toFixed(0)),
      moduleWidthInInch: Number((moduleWidthAfterBwr / resolution).toFixed(3)),
      moduleWidthInMM: Number(((moduleWidthAfterBwr * 25.4) / resolution).toFixed(3)),
      moduleWidth: (moduleWidthAfterBwr / resolution).toFixed(3),
      qrWidth: (qrWidthPixel / resolution).toFixed(3),
      qrWidthInch: Number((qrWidthPixel / resolution).toFixed(3)),
      qrWidthMM: Number(((qrWidthPixel * 25.4) / resolution).toFixed(3)),
      bwr: bwr,
    };
  }

  async create(config, isSavingConfig = false, isDownload = false, apiPath = null) {
    const QRImageFormData = config?.qrImageFormData? JSON.parse( config.qrImageFormData): {}

    if(QRImageFormData.colorMode === "HEXA"){  // hack because of the image service doesn't allow hexa as a colorMode.
      QRImageFormData.colorMode = "rgb"
    }

    // Convert mm to cm for processing
    if(QRImageFormData.unitsOfMeasure === "mm") {
      if(QRImageFormData.size) {
        QRImageFormData.size = QRImageFormData.size / 10
      }
      if(QRImageFormData.moduleWidth) {
        QRImageFormData.moduleWidth = QRImageFormData.moduleWidth / 10
      }
      if(QRImageFormData.bwr) {
        QRImageFormData.bwr = QRImageFormData.bwr / 10
      }
      QRImageFormData.unitsOfMeasure = "cm"
    }

    // if(!isDefaultDesignerConfig){
    //   let shorturl = config?.shortUrl? config.shortUrl: "Th1s 1s @ s@mpl3 QR v@lu3"
    //   // use tec-it api to create the qr code, if it is not for downloading, changing the image format to PNG for previewing
    //   if(!isDownload) {
    //     QRImageFormData.imageFormat = "PNG"
    //   }
    //   return await this.createTecItQR(QRImageFormData, isSavingConfig, shorturl, this.v1ImageConfig)
    // } else {
     const baseConfig = this.defaultImageConfig
     let captionsInfo = {}
    //   try {
    //     if(config.qrImageFormData){
    //       captionsInfo = this.processCaptionData(QRImageFormData, baseConfig, isSavingConfig)
    //     }
    //   } catch (e) {
    //     new Notification()
    //       .setType("success")
    //       .setMessage("Failed to preview QR Image")
    //       .send();
    //     console.log("error", e)
    //     return null
    //   }
    //   this.processLogoData(QRImageFormData)
    //   if (QRImageFormData?.isDefaultDesigner) {
    //     // temp for color mode, needto be removed after api fixed cmyk issue
    //     QRImageFormData.colorMode = "rgb"
    //   }
      if (QRImageFormData?.transparentBackground){QRImageFormData.backgroundColor="transparent"}

      let updatedConfigWithBase = {...baseConfig, ...QRImageFormData, ...captionsInfo}

      // Remove the conflicting size/moduleWidth key based on showImageSizeOrModuleWidth.
      // true (or absent, server default) = image size mode → keep size, remove moduleWidth.
      // false = module width mode → keep moduleWidth, remove size.
      if (updatedConfigWithBase.showImageSizeOrModuleWidth === false) {
        delete updatedConfigWithBase.size
      } else {
        delete updatedConfigWithBase.moduleWidth
      }

      if(QRImageFormData.imageFormat != baseConfig.imageFormat){
        updatedConfigWithBase.imageFormat = QRImageFormData.imageFormat
      }

      if(QRImageFormData.colorMode != null) {
        updatedConfigWithBase.colorMode = QRImageFormData.colorMode
      }

      // We don't need to treat old image config differently, we can ignore below comment codes
      // this IF is for old image config
      // if(QRImageFormData.logo) {
      //   const imageType = getImageType(QRImageFormData.logo)
      //   // if (imageType === "application/pdf") {
      //   //   updatedConfigWithBase.colorMode = "cmyk"
      //   //   updatedConfigWithBase.imageFormat = "pdf"
      //   // }
      //   // if (imageType === "application/postscript") {
      //   //   updatedConfigWithBase.colorMode = "cmyk"
      //   //   updatedConfigWithBase.imageFormat = "eps"
      //   // }
      //   // if it only previews image and color mode is cmyk, then image format should be jpg
      //   if (updatedConfigWithBase.colorMode === "cmyk" && !isDownload) {
      //     updatedConfigWithBase.imageFormat = "jpg"
      //   }
      // }

      // Process image format based on color mode and download/save flags
      updatedConfigWithBase = this.processImageFormat(updatedConfigWithBase, QRImageFormData, isDownload, isSavingConfig)

      if(updatedConfigWithBase.colorMode === "rgb" && !updatedConfigWithBase.backgroundColor ){
        updatedConfigWithBase.backgroundColor = "#ffffff"
      }
      const dataPayload = {
        "data": `${config?.shortUrl}`,
      }
      const payload = {...updatedConfigWithBase, ...dataPayload}
      const payloadCMYKConversion = await this.convertCMYKStringsToArray(payload.isCMYK? await this.convertHexInPayloadToCMYK(payload): payload)
      try {
        const url = apiPath || this.url;
        const res = await this.httpClient.post(url, payloadCMYKConversion);
        let previewImage = processImageServiceResponse(res)
        if (!isDownload) {
          previewImage = this.correctPreviewImageInfo(previewImage, QRImageFormData.imageFormat, payloadCMYKConversion)
        }
        return previewImage
      } catch (e) {
        new Notification()
          .setType("error")
          .setMessage(`Failed to generate QR Image: ${e.message}`)
          .send();
        return null;
      }
    //}
  }

  buildV1Config(qrImageFormData, isSavingConfig = false, shorturl, v1ImageConfig) {
    let baseConfig = JSON.parse(JSON.stringify(v1ImageConfig))
    if(qrImageFormData.imageFormat) {
      if(qrImageFormData.imageFormat === "svg+xml"){
        //baseConfig.setImageFormat("SVG")
        baseConfig.imageFormat = "SVG"
      } else {
        //baseConfig.setImageFormat(qrImageFormData.imageFormat.toUpperCase())
        baseConfig.imageFormat = qrImageFormData.imageFormat.toUpperCase()
      }

    }
    if(qrImageFormData.resolution){
      //baseConfig.setResolution(qrImageFormData.resolution.toString())
      baseConfig.resolution = qrImageFormData.resolution.toString()
    } else {
      //baseConfig.setResolution("1200")
      baseConfig.resolution = "1200"
    }
    if(qrImageFormData.hasOwnProperty("showImageSizeOrModuleWidth") && !qrImageFormData.showImageSizeOrModuleWidth) {
      if(qrImageFormData.moduleWidth) {
        baseConfig.moduleWidth = qrImageFormData.moduleWidth.toString()
        // remove barcodeSize from baseConfig
      } else {
        baseConfig.moduleWidth = "1"
      }
      delete baseConfig.barcodeSize
    } else {
      if(qrImageFormData.size && qrImageFormData.size !== "0") {
      // baseConfig.setBarcodeSize(qrImageFormData.size.toString())
        baseConfig.barcodeSize = qrImageFormData.size.toString()
      } else {
        baseConfig.barcodeSize = "1"
      }
      delete baseConfig.moduleWidth
    }

    if(qrImageFormData.unitsOfMeasure){
      baseConfig.barcodeSizeUnit = qrImageFormData.unitsOfMeasure.toString()
      baseConfig.reductionUnit = qrImageFormData.unitsOfMeasure.toString()
      if(qrImageFormData.unitsOfMeasure === "cm") {
        console.log("Converting barcodeSize from cm to mm");
        // baseConfig.setBarcodeSize((qrImageFormData.size * 10).toString())
        if(qrImageFormData.hasOwnProperty("showImageSizeOrModuleWidth") && !qrImageFormData.showImageSizeOrModuleWidth) {
          baseConfig.moduleWidth = (baseConfig.moduleWidth * 10).toString()
        } else {
          baseConfig.barcodeSize = (baseConfig.barcodeSize * 10).toString()
        }
        baseConfig.barcodeSizeUnit = "mm"
        baseConfig.reductionUnit = "mm"
      } else if(qrImageFormData.unitsOfMeasure === "mm") {
        // mm is already in mm, no conversion needed
        baseConfig.barcodeSizeUnit = "mm"
        baseConfig.reductionUnit = "mm"
      }
    }


    if(qrImageFormData.quietZone){
      // baseConfig.setQuietSpace(qrImageFormData.quietZone.toString())
      baseConfig.quietSpace = qrImageFormData.quietZone.toString()
    }
    if(qrImageFormData.colorMode){
      //baseConfig.setColorMode(qrImageFormData.colorMode)
      baseConfig.colorMode = qrImageFormData.colorMode
    }
    if(qrImageFormData.foregroundColor) {
      let color = ''
      if(qrImageFormData.colorMode === "cmyk"){
        if(qrImageFormData.foregroundColor.startsWith("cmyk")){
          color = this.convertV1CmykValues(qrImageFormData.foregroundColor)
        } else {
          color = this.hexToCmyk(qrImageFormData.foregroundColor)
        }
      }
      if(qrImageFormData.colorMode === "rgb"){
        color = this.hexToRgb(qrImageFormData.foregroundColor)
      }
      // baseConfig.setBarcodeColor(color)
      baseConfig.barcodeColor = color
    }
    if(qrImageFormData.bwr) {
      // baseConfig.setReduction(qrImageFormData.bwr.toString())
      baseConfig.reduction = qrImageFormData.bwr.toString()
    }
    // baseConfig.setData(shorturl)
    baseConfig.data = shorturl
    return baseConfig
  }

  async createTecItQR(qrImageFormData, isSavingConfig = false, shorturl, v1ImageConfig) {
    const baseConfig = this.buildV1Config(qrImageFormData, isSavingConfig, shorturl, v1ImageConfig)
    const v1HttpClient = new HttpClient(configs.V1_COMPACT_QR_API_URL);
    let path = "/qr";
    if(qrImageFormData.barcodeFormat && qrImageFormData.barcodeFormat === barcodeFormatConstants.DATA_MATRIX) {
      path = "/datamatrix";
    }
    const res = await v1HttpClient.post(`/v2${path}`, baseConfig);
    if (res.data.codeData) {
      const qrImageBase64 = res.data.codeData
      let qrImage = {
        base64Image: qrImageBase64,
        status: 201,
        moduleWidth: res.data.imageInfo.moduleWidth ? res.data.imageInfo.moduleWidth : '',
        qrWidth: res.data.imageInfo.qrWidth ? res.data.imageInfo.qrWidth : '',
        qrWidthPixel: res.data.imageInfo.qrWidthInPixel ? res.data.imageInfo.qrWidthInPixel : '',
        qrWidthInch: res.data.imageInfo.qrWidthInInch ? res.data.imageInfo.qrWidthInInch : '',
        qrWidthMM: res.data.imageInfo.qrWidthInMM ? res.data.imageInfo.qrWidthInMM : '',
        qrVersion: res.data.imageInfo.qrVersion ? res.data.imageInfo.qrVersion : '',
        modulePerSide: res.data.imageInfo.modulePerSide ? res.data.imageInfo.modulePerSide : '',
        errorCorrection: res.data.imageInfo.errorCorrectionLevel ? res.data.imageInfo.errorCorrectionLevel : '',
        unit: res.data.imageInfo.unit ? res.data.imageInfo.unit : '',
        quietZone: res.data.imageInfo.quietZone ? res.data.imageInfo.quietZone : '',
        moduleWidthInPixel: res.data.imageInfo.moduleWidthInPixel ? res.data.imageInfo.moduleWidthInPixel : '',
        moduleWidthInInch: res.data.imageInfo.moduleWidthInInch ? res.data.imageInfo.moduleWidthInInch : '',
        moduleWidthInMM: res.data.imageInfo.moduleWidthInMM ? res.data.imageInfo.moduleWidthInMM : ''
      }
      //qrImage.data = qrImage.data.slice(1, -1);
      return qrImage
    }
  }

  hexToRgb(hex) {
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
  }

  rgbToCmyk(rgb) {
    var parts = rgb.split(',').map(Number);
    var r = parts[0];
    var g = parts[1];
    var b = parts[2];

    if (r == 0 && g == 0 && b == 0) {
      return "0,0,0,100"; // Pure black
    }

    var c = 1 - (r / 255);
    var m = 1 - (g / 255);
    var y = 1 - (b / 255);
    var k = Math.min(c, m, y);

    c = Math.round(((c - k) / (1 - k)) * 100);
    m = Math.round(((m - k) / (1 - k)) * 100);
    y = Math.round(((y - k) / (1 - k)) * 100);
    k = Math.round(k * 100);

    return `${c},${m},${y},${k}`;
  }

  hexToCmyk(hex) {
    var rgb = this.hexToRgb(hex);
    return this.rgbToCmyk(rgb);
  }

  convertV1CmykValues(cmykString) {
    // Remove the "cmyk(" prefix, the trailing ")", and any "%" signs
    return cmykString
      .replace(/^cmyk\(/i, "") // Remove "cmyk("
      .replace(/\)$/, "")      // Remove closing ")"
      .replace(/%/g, "")       // Remove all "%"
      .trim()                  // Trim any extra spaces
      .replace(/\s+/g, "");    // remove all spaces;
  }

  // processCaptionData(data, isSavingConfig){
  //   const newCaptionData = {};
  //   for (const key in data) {
  //     //if (typeof data[key] === 'string') {
  //         const captionData = data[key];
  //         if (captionData.hasOwnProperty('captionTextPosition')) {
  //           if (captionData.captionTextPosition === 'Top' && !newCaptionData.hasOwnProperty('topText')) {
  //             let fontSize = captionData.captionFontSize
  //             if(isSavingConfig){
  //               fontSize = Math.floor(fontSize * 0.1)
  //             }
  //             newCaptionData.topText = captionData.captionContent;
  //             newCaptionData.topTextSize = fontSize;
  //             newCaptionData.topTextColor = captionData.captionColor;
  //           } else if (captionData.captionTextPosition === 'Bottom' && !newCaptionData.hasOwnProperty('bottomText')) {
  //             let fontSize = captionData.captionFontSize
  //             if(isSavingConfig){
  //               fontSize = Math.floor(fontSize * 0.1)
  //             }
  //             newCaptionData.bottomText = captionData.captionContent;
  //             newCaptionData.bottomTextSize = fontSize;
  //             newCaptionData.bottomTextColor = captionData.captionColor;
  //           } else if(captionData.captionTextPosition === 'Left' && !newCaptionData.hasOwnProperty('leftText')) {
  //             let fontSize = captionData.captionFontSize
  //             if(isSavingConfig){
  //               fontSize = Math.floor(fontSize * 0.1)
  //             }
  //             newCaptionData.leftText = captionData.captionContent;
  //             newCaptionData.leftTextSize = fontSize;
  //             newCaptionData.leftTextColor = captionData.captionColor;
  //           } else if(captionData.captionTextPosition === 'Right' && !newCaptionData.hasOwnProperty('rightText')) {
  //             let fontSize = captionData.captionFontSize
  //             if(isSavingConfig){
  //               fontSize = Math.floor(fontSize * 0.1)
  //             }
  //             newCaptionData.rightText = captionData.captionContent;
  //             newCaptionData.rightTextSize = fontSize;
  //             newCaptionData.rightTextColor = captionData.captionColor;
  //           } else if(captionData.captionTextPosition === 'Top' && newCaptionData.hasOwnProperty('topText')) {
  //             throw new Error('Top caption already exists')
  //           } else if(captionData.captionTextPosition === 'Bottom' && newCaptionData.hasOwnProperty('bottomText')) {
  //             throw new Error('Bottom caption already exists')
  //           } else if(captionData.captionTextPosition === 'Left' && newCaptionData.hasOwnProperty('leftText')) {
  //             throw new Error('Left caption already exists')
  //           } else if(captionData.captionTextPosition === 'Right' && newCaptionData.hasOwnProperty('rightText')) {
  //             throw new Error('Right caption already exists')
  //           }
  //         }

  //    // }
  //   }
  //   return newCaptionData
  // }

  processCaptionData(data, defaultImageConfig, isSavingConfig) {
    const newCaptionData = {};

    const positionKeys = defaultImageConfig.positionKeys;

    for (const key in data) {
      const captionData = data[key];
      const captionTextPosition = captionData.captionTextPosition;

      if (captionData.hasOwnProperty('captionTextPosition') && positionKeys.hasOwnProperty(captionTextPosition)) {
        const positionKey = positionKeys[captionTextPosition];

        if (newCaptionData.hasOwnProperty(positionKey)) {
          throw new Error(`${captionTextPosition} caption already exists`);
        }

        let fontSize = captionData.captionFontSize;
        if (isSavingConfig) {
          fontSize = Math.floor(fontSize * 0.1);
        }

        newCaptionData[positionKey] = captionData.captionContent;
        newCaptionData[`${positionKey}Size`] = fontSize;
        newCaptionData[`${positionKey}Color`] = captionData.captionColor;
      }
    }

    return newCaptionData;
  }

  // filter logo data,
  // if logoType is emptyImage, delete backgroundImage
  // if logoType is logoImage, delete backgroundImage
  // if logoType is backgroundImage, delete logo
  processLogoData(data){
    if(data.hasOwnProperty("logoType")){
      if(data["logoType"] === "emptyImage"){
        data["logo"] = "empty"
      }
    }

  }


  // async fetch(data){
  //
  //   try {
  //     const res = await this.httpClient.get(this.url, data);
  //     if (res.data) {
  //       return res.data
  //     }
  //   }catch (e){
  //     if(e.message.includes("timeout of 1000ms exceeded")){
  //       console.log("try again")
  //       await this.create()
  //     }
  //     console.log("e", e)
  //   }
  // }
}


export default PreviewManager
